import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
import "./NotFound.css";

const NotFound = () => {
  const navigate = useNavigate();
  const tiltRef = useRef(null);

  useEffect(() => {
    const el = tiltRef.current;
    if (!el) return;

    // Respect reduced motion and avoid extra listeners on touch devices
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const isCoarsePointer =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(pointer: coarse)").matches;

    if (reduceMotion || isCoarsePointer) return;

    let raf = 0;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width; // 0..1
      const y = (e.clientY - rect.top) / rect.height; // 0..1

      // map to degrees
      const ry = (x - 0.5) * 18; // -9..9
      const rx = (0.5 - y) * 18; // -9..9

      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.setProperty("--nf-rx", `${rx.toFixed(2)}deg`);
        el.style.setProperty("--nf-ry", `${ry.toFixed(2)}deg`);
      });
    };

    const onLeave = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.setProperty("--nf-rx", `0deg`);
        el.style.setProperty("--nf-ry", `0deg`);
      });
    };

    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  return (
    <div className="nf404">
      <Helmet>
        <title>404 - Page Not Found | Marichi Tech AI</title>
        <meta name="robots" content="noindex, follow" />
        <meta
          name="description"
          content="The page you are looking for does not exist or has been moved."
        />
      </Helmet>

      <div className="nf404__bg" aria-hidden="true">
        <div className="nf404__grid" />
        <div className="nf404__orbs">
          <span className="nf404__orb nf404__orb--1" />
          <span className="nf404__orb nf404__orb--2" />
          <span className="nf404__orb nf404__orb--3" />
        </div>
      </div>

      <main className="nf404__content" ref={tiltRef}>
        <div className="nf404__stage" aria-hidden="true">
          <div className="nf404__cube">
            <div className="nf404__face nf404__face--front">4</div>
            <div className="nf404__face nf404__face--back">4</div>
            <div className="nf404__face nf404__face--right">0</div>
            <div className="nf404__face nf404__face--left">0</div>
            <div className="nf404__face nf404__face--top">4</div>
            <div className="nf404__face nf404__face--bottom">0</div>
          </div>
          <div className="nf404__shadow" />
        </div>

        <h1 className="nf404__title">Page not found</h1>
        <p className="nf404__subtitle">
          The URL you opened doesn’t exist here. Try the home page, or head back
          to where you were.
        </p>

        <div className="nf404__actions">
          <Link className="nf404__btn nf404__btn--primary" to="/">
            Go home
          </Link>
          <button
            className="nf404__btn nf404__btn--ghost"
            type="button"
            onClick={() => navigate(-1)}
          >
            Go back
          </button>
        </div>

        <div className="nf404__links">
          <span>Popular:</span>
          <Link to="/career">Career</Link>
          <Link to="/services">Services</Link>
          <Link to="/contact">Contact</Link>
        </div>
      </main>
    </div>
  );
};

export default NotFound;
